import React from "react";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Zap, Loader2, Calendar, Target, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";

export function ActionPlanTimeline({ scan, resultsLoading }) {
  const isProcessing = scan?.section_statuses?.action_plan?.status === "processing";
  const isFailed = scan?.section_statuses?.action_plan?.status === "failed";
  const planItems = Array.isArray(scan?.action_plan) ? scan.action_plan.slice(0, 10) : [];
  const terminal = ["completed", "partial", "failed"].includes(scan?.status);

  if (resultsLoading || (isProcessing && !terminal && !planItems.length)) {
    return (
      <Card className="p-12 flex flex-col items-center justify-center text-center">
        <Loader2 className="h-8 w-8 text-[#FEAB3D] animate-spin mb-4" />
        <p className="font-medium text-slate-700">Generating 30-Day Action Plan…</p>
        <p className="text-sm text-slate-500 mt-2">
          Prioritizing the highest-impact fixes for your site.
        </p>
      </Card>
    );
  }

  if (isFailed && !planItems.length) {
    return (
      <Card className="p-8 text-center bg-red-50 border-red-200">
        <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-3" />
        <p className="font-medium text-red-700">Failed to generate Action Plan</p>
        <p className="text-sm text-red-600 mt-1">Please retry this section.</p>
      </Card>
    );
  }

  if (!planItems.length) {
    return (
      <Card className="p-8 text-center text-slate-500">
        No actionable findings are available in this report. Re-scan to collect more evidence.
      </Card>
    );
  }

  const grouped = {
    today: planItems.filter(i => i.timing === "today"),
    this_week: planItems.filter(i => i.timing === "this_week"),
    this_month: planItems.filter(i => (!i.timing || !["today", "this_week", "longer_term"].includes(i.timing))),
    longer_term: planItems.filter(i => i.timing === "longer_term"),
  };

  const timingConfig = {
    today: { label: "Today / Quick Wins", icon: Zap, color: "text-amber-500", bg: "bg-amber-100" },
    this_week: { label: "This Week", icon: Target, color: "text-[#FEAB3D]", bg: "bg-[#FEAB3D]/20" },
    this_month: { label: "This Month", icon: Calendar, color: "text-[#FEAB3D]", bg: "bg-[#FEAB3D]/20" },
    longer_term: { label: "Longer Term", icon: Clock, color: "text-slate-500", bg: "bg-slate-100" },
  };




  return (
    <div className="space-y-8">
      {Object.entries(grouped).map(([timingKey, items]) => {
        if (items.length === 0) return null;
        const config = timingConfig[timingKey];
        const Icon = config.icon || CheckCircle2;
        
        return (
          <div key={timingKey} className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-full ${config.bg}`}>
                <Icon className={`h-5 w-5 ${config.color}`} />
              </div>
              <h3 className="text-lg font-bold text-slate-800">{config.label}</h3>
              <Badge variant="secondary" className="ml-2">{items.length} tasks</Badge>
            </div>
            
            <div className="space-y-3 ml-4 pl-4 border-l-2 border-slate-100">
              {items.map((item, idx) => (
                <Card key={idx} className="p-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h4 className="font-semibold text-slate-800 text-base">{item.task || item.title}</h4>
                      <p className="text-sm text-slate-600 mt-1">{item.reason}</p>
                      {item.related_issue && <p className="text-xs text-slate-500 mt-2">Related issue: {item.related_issue}</p>}
                      
                      <div className="flex flex-wrap items-center gap-4 mt-4">
                        <div className="text-xs">
                          <span className="text-slate-400 font-medium">Impact: </span>
                          <span className="text-slate-700 capitalize">{item.impact}</span>
                        </div>
                        <div className="text-xs">
                          <span className="text-slate-400 font-medium">Effort: </span>
                          <span className="text-slate-700 capitalize">{item.effort}</span>
                        </div>
                        {item.affected_url && (
                          <div className="text-xs">
                            <span className="text-slate-400 font-medium">URL: </span>
                            <span className="text-[#FEAB3D] break-all">
                              {item.affected_url}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <Badge className={
                        (item.priority || "medium").toLowerCase() === "critical" ? "bg-red-100 text-red-700 border-red-200" :
                        (item.priority || "medium").toLowerCase() === "high" ? "bg-amber-100 text-amber-700 border-amber-200" :
                        "bg-slate-100 text-slate-700"
                      }>
                        {item.priority || "medium"}
                      </Badge>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
